import { BarChart3 } from 'lucide-react';
import { MetricKey, MetricToggle, DEFAULT_METRIC_TOGGLES } from '@/types/reports';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

interface MetricChipSelectorProps {
  enabledMetrics: MetricKey[];
  onMetricsChange: (metrics: MetricKey[]) => void;
  disabled?: boolean;
  /** When true, the entire section appears greyed out and non-interactive (e.g., chart section not active) */
  inactive?: boolean;
}

export function MetricChipSelector({
  enabledMetrics,
  onMetricsChange,
  disabled = false,
  inactive = false,
}: MetricChipSelectorProps) {
  // Combine disabled and inactive for interaction blocking
  const isDisabled = disabled || inactive;
  const handleToggle = (metricKey: MetricKey, checked: boolean) => {
    if (checked) {
      onMetricsChange([...enabledMetrics, metricKey]);
    } else {
      onMetricsChange(enabledMetrics.filter(k => k !== metricKey));
    }
  };

  const handleSelectAll = () => {
    const allMetricKeys = DEFAULT_METRIC_TOGGLES.map(m => m.key);
    onMetricsChange(allMetricKeys);
  };

  const handleClearMetrics = () => {
    onMetricsChange([]);
  };

  const allSelected = enabledMetrics.length === DEFAULT_METRIC_TOGGLES.length;
  const noneSelected = enabledMetrics.length === 0;

  return (
    <div className={cn(
      "space-y-2 transition-opacity duration-200",
      inactive && "opacity-40 pointer-events-none"
    )}>
      {/* Label with All | Clear links */}
      <div className="flex items-center justify-between">
        <span className={cn(
          "text-xs text-muted-foreground uppercase tracking-wider font-medium flex items-center gap-2",
          inactive && "text-muted-foreground/60"
        )}>
          <BarChart3 className={cn("h-4 w-4", inactive ? "text-muted-foreground/40" : "text-[#8FB560]")} />
          Chart Metrics
        </span>
        <div className="flex items-center gap-1 text-xs">
          <button
            type="button"
            onClick={handleSelectAll}
            disabled={isDisabled || allSelected}
            className={cn(
              "hover:text-foreground transition-colors",
              allSelected || isDisabled
                ? "text-muted-foreground/40 cursor-not-allowed"
                : "text-muted-foreground hover:underline"
            )}
          >
            All
          </button>
          <span className="text-muted-foreground/40">|</span>
          <button
            type="button"
            onClick={handleClearMetrics}
            disabled={isDisabled || noneSelected}
            className={cn(
              "hover:text-foreground transition-colors",
              noneSelected || isDisabled
                ? "text-muted-foreground/40 cursor-not-allowed"
                : "text-muted-foreground hover:underline"
            )}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Inactive hint when chart not selected */}
      {inactive && (
        <p className="text-[10px] text-muted-foreground/50 italic">
          Enable Chart section to configure
        </p>
      )}

      {/* 2-column grid of all metrics */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
        {DEFAULT_METRIC_TOGGLES.map((metric: MetricToggle) => {
          const isEnabled = enabledMetrics.includes(metric.key);

          return (
            <label
              key={metric.key}
              htmlFor={`metric-${metric.key}`}
              className={cn(
                "flex items-center justify-between gap-2 py-0.5",
                isDisabled ? "cursor-not-allowed" : "cursor-pointer"
              )}
            >
              {/* Left side: dot + label */}
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className={cn(
                    "w-2 h-2 rounded-full flex-shrink-0 transition-opacity",
                    inactive && "opacity-40"
                  )}
                  style={{ backgroundColor: metric.color }}
                />
                <span className={cn(
                  "text-xs truncate",
                  inactive ? "text-muted-foreground/50" : "text-foreground/80"
                )}>
                  {metric.label}
                </span>
              </div>

              {/* Right side: checkbox */}
              <Checkbox
                id={`metric-${metric.key}`}
                checked={isEnabled}
                onCheckedChange={(checked) => handleToggle(metric.key, checked === true)}
                disabled={isDisabled}
                className={cn(
                  "data-[state=checked]:bg-[#8FB560] data-[state=checked]:border-[#8FB560] flex-shrink-0",
                  inactive && "opacity-50"
                )}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
}
